// =====================================================
// HELP CENTER + HELP CHAT API (help-center-chatbot)
// =====================================================
// Points at /landlord/api/help/* (apps/api/src/routes/manager/help.routes.ts). Every type is derived
// from the boundary schemas in @resida/api-types/help. The articles, search, categories and topic reads (DC-2)
// are always available; the conversation routes (DC-3) answer 503 HELP_CHAT_DISABLED while the chat
// kill switch is off (NFR-OBSERVABILITY-6), which callers detect with isHelpChatDisabled().

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  HELP_CHAT_DISABLED_CODE,
  type ArticleResponse,
  type ArticleSummary,
  type CategoryArticles,
  type CategoryItem,
  type ConversationCreated,
  type ConversationListItem,
  type ConversationMessages,
  type EscalationDraft,
  type PostMessageBody,
  type RatingBody,
  type RatingResponse,
  type SearchResult,
} from '@resida/api-types/help';
import { ApiClientError, apiFetch, apiStream, type ApiStreamOptions } from './api.core';

const HELP_API = '/landlord/api/help';

/** Query keys. Every help query lives under `helpKeys.all` so a sign-out can drop them in one call. */
export const helpKeys = {
  all: ['help'] as const,
  screenArticles: (pathname: string) => ['help', 'articles', pathname] as const,
  article: (id: string) => ['help', 'article', id] as const,
  search: (q: string) => ['help', 'search', q] as const,
  categories: ['help', 'categories'] as const,
  categoryArticles: (category: string) => ['help', 'category', category] as const,
  conversations: ['help', 'conversations'] as const,
};

/** The in-app path of an article (BR-1): `/help/<id>`. */
export function helpArticlePath(articleId: string): string {
  return `/help/${articleId}`;
}

/** The in-app path of a topic (a category's article list): `/help/topics/<category>`. */
export function helpTopicPath(category: string): string {
  return `/help/topics/${category}`;
}

/** True when a chat route refused the call because the chat kill switch is off. */
export function isHelpChatDisabled(error: unknown): boolean {
  return (
    error instanceof ApiClientError && error.status === 503 && error.serverCode === HELP_CHAT_DISABLED_CODE
  );
}

// ----- DC-2: articles, search, categories -----------------------------------------------------

function getScreenArticles(pathname: string): Promise<ArticleSummary[]> {
  return apiFetch<ArticleSummary[]>(`${HELP_API}/articles?path=${encodeURIComponent(pathname)}`);
}

function getHelpArticle(id: string): Promise<ArticleResponse> {
  return apiFetch<ArticleResponse>(`${HELP_API}/articles/${encodeURIComponent(id)}`);
}

function searchHelp(q: string): Promise<SearchResult[]> {
  return apiFetch<SearchResult[]>(`${HELP_API}/search?q=${encodeURIComponent(q)}`);
}

function getHelpCategories(): Promise<CategoryItem[]> {
  return apiFetch<CategoryItem[]>(`${HELP_API}/categories`);
}

function getHelpCategoryArticles(category: string): Promise<CategoryArticles> {
  return apiFetch<CategoryArticles>(`${HELP_API}/categories/${encodeURIComponent(category)}/articles`);
}

const ARTICLE_STALE_MS = 5 * 60_000;

/** The articles for the landlord's current screen. Disabled until the panel or help center is open (NFR-PERF-4). */
export function useScreenArticles(pathname: string, enabled: boolean) {
  return useQuery({
    queryKey: helpKeys.screenArticles(pathname),
    queryFn: () => getScreenArticles(pathname),
    enabled,
    staleTime: ARTICLE_STALE_MS,
  });
}

export function useHelpArticle(id: string | null) {
  return useQuery({
    queryKey: helpKeys.article(id ?? ''),
    queryFn: () => getHelpArticle(id ?? ''),
    enabled: id !== null,
    staleTime: ARTICLE_STALE_MS,
    retry: false,
  });
}

/** Ranked search results; an empty query is never sent (BR-13: it shows the categories instead). */
export function useHelpSearch(q: string) {
  const query = q.trim();
  return useQuery({
    queryKey: helpKeys.search(query),
    queryFn: () => searchHelp(query),
    enabled: query.length > 0,
    staleTime: ARTICLE_STALE_MS,
    placeholderData: keepPreviousData,
  });
}

export function useHelpCategories(enabled: boolean) {
  return useQuery({
    queryKey: helpKeys.categories,
    queryFn: getHelpCategories,
    enabled,
    staleTime: ARTICLE_STALE_MS,
  });
}

/** One category's current articles: the topic view behind a topic card or panel row. A 404 means the topic is gone. */
export function useHelpCategoryArticles(category: string | null) {
  return useQuery({
    queryKey: helpKeys.categoryArticles(category ?? ''),
    queryFn: () => getHelpCategoryArticles(category ?? ''),
    enabled: category !== null,
    staleTime: ARTICLE_STALE_MS,
    retry: false,
  });
}

// ----- DC-3: conversations ----------------------------------------------------------------------

export function createHelpConversation(): Promise<ConversationCreated> {
  return apiFetch<ConversationCreated>(`${HELP_API}/conversations`, { method: 'POST' });
}

function listHelpConversations(): Promise<ConversationListItem[]> {
  return apiFetch<ConversationListItem[]>(`${HELP_API}/conversations`);
}

export function getHelpConversation(id: string): Promise<ConversationMessages> {
  return apiFetch<ConversationMessages>(`${HELP_API}/conversations/${id}`);
}

function deleteHelpConversation(id: string): Promise<void> {
  return apiFetch<void>(`${HELP_API}/conversations/${id}`, { method: 'DELETE' });
}

/** The contact-form draft for a conversation (DC-6). Nothing is sent; the landlord submits the form. */
export function getEscalationDraft(conversationId: string): Promise<EscalationDraft> {
  return apiFetch<EscalationDraft>(`${HELP_API}/conversations/${conversationId}/escalation`, { method: 'POST' });
}

/**
 * Posts a message and streams the reply: `token` events then exactly one `final` event (DC-3). Failures
 * before the first byte (validation, auth, ownership, kill switch) reject with an ApiClientError.
 */
export function streamHelpMessage(
  conversationId: string,
  body: PostMessageBody,
  options: ApiStreamOptions
): Promise<void> {
  return apiStream(`${HELP_API}/conversations/${conversationId}/messages`, body, options);
}

function rateHelpMessage(messageId: string, body: RatingBody): Promise<RatingResponse> {
  return apiFetch<RatingResponse>(`${HELP_API}/messages/${messageId}/rating`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** The caller's conversations, newest first. Doubles as the chat-availability probe (503 HELP_CHAT_DISABLED). */
export function useHelpConversations(enabled: boolean) {
  return useQuery({
    queryKey: helpKeys.conversations,
    queryFn: listHelpConversations,
    enabled,
    retry: (failureCount, error) => !isHelpChatDisabled(error) && failureCount < 1,
  });
}

export function useDeleteHelpConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteHelpConversation,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: helpKeys.conversations }),
  });
}

export function useRateHelpMessage() {
  return useMutation({
    mutationFn: ({ messageId, ...body }: RatingBody & { messageId: string }) => rateHelpMessage(messageId, body),
  });
}
